const API_URL = process.env.REACT_APP_BACKEND_URL;

class ApiService {
  constructor() {
    this.baseUrl = `${API_URL}/api`;
  }

  getToken() {
    return localStorage.getItem("auth_token");
  }

  setAuth(token, user) {
    localStorage.setItem("auth_token", token);
    localStorage.setItem("auth_user", JSON.stringify(user));
  }

  clearAuth() {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("auth_user");
  }

  getUser() {
    const u = localStorage.getItem("auth_user");
    return u ? JSON.parse(u) : null;
  }

  async request(path, options = {}) {
    const token = this.getToken();
    const headers = { "Content-Type": "application/json", ...options.headers };
    if (token) headers["Authorization"] = `Bearer ${token}`;

    const res = await fetch(`${this.baseUrl}${path}`, { ...options, headers });
    if (res.status === 401) {
      this.clearAuth();
      // Redirect based on current path
      const currentPath = window.location.pathname;
      if (currentPath.startsWith('/admin')) {
        window.location.href = "/admin/login";
      } else if (currentPath.startsWith('/agent')) {
        window.location.href = "/agent/login";
      } else {
        window.location.href = "/login";
      }
      throw new Error("Unauthorized");
    }
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: "Request failed" }));
      throw new Error(err.detail || "Request failed");
    }
    return res.json();
  }

  // Auth
  async login(email, password) {
    const data = await this.request("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    if (data.access_token && data.user) {
      this.setAuth(data.access_token, data.user);
    }
    return data;
  }

  register(data) {
    return this.request("/auth/register", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  getMe() {
    return this.request("/auth/me");
  }

  // Admin
  getAdminStats() {
    return this.request("/admin/stats");
  }

  getUsers() {
    return this.request("/admin/users");
  }

  createUser(data) {
    return this.request("/admin/users", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  updateUser(userId, data) {
    return this.request(`/admin/users/${userId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  deleteUser(userId) {
    return this.request(`/admin/users/${userId}`, { method: "DELETE" });
  }

  getAdminAssessments() {
    return this.request("/admin/assessments");
  }

  createAssessment(data) {
    return this.request("/admin/assessments", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  updateAssessment(id, data) {
    return this.request(`/admin/assessments/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  deleteAssessment(id) {
    return this.request(`/admin/assessments/${id}`, { method: "DELETE" });
  }

  getMilestones(assessmentId) {
    return this.request(`/admin/assessments/${assessmentId}/milestones`);
  }

  createMilestone(assessmentId, data) {
    return this.request(`/admin/assessments/${assessmentId}/milestones`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  updateMilestone(id, data) {
    return this.request(`/admin/milestones/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  deleteMilestone(id) {
    return this.request(`/admin/milestones/${id}`, { method: "DELETE" });
  }

  getQuestionsByMilestone(milestoneId) {
    return this.request(`/admin/milestones/${milestoneId}/questions`);
  }

  createQuestion(milestoneId, data) {
    return this.request(`/admin/milestones/${milestoneId}/questions`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  updateQuestion(id, data) {
    return this.request(`/admin/questions/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  deleteQuestion(id) {
    return this.request(`/admin/questions/${id}`, { method: "DELETE" });
  }

  getAdminExecutions(status) {
    const q = status ? `?status=${status}` : "";
    return this.request(`/admin/executions${q}`);
  }

  getAdminExecution(id) {
    return this.request(`/admin/executions/${id}`);
  }

  approveExecution(id, comment) {
    return this.request(`/admin/executions/${id}/approve`, {
      method: "POST",
      body: JSON.stringify({ comment: comment || "" }),
    });
  }

  markExecutionPaid(id) {
    return this.request(`/admin/executions/${id}/mark-paid`, { method: "POST" });
  }

  requestReworkQuestions(executionId, reworkItems) {
    return this.request(`/admin/executions/${executionId}/rework-questions`, {
      method: "POST",
      body: JSON.stringify({ rework_items: reworkItems }),
    });
  }

  // Client dashboard
  getMyExecutions() {
    return this.request("/client/my-executions");
  }

  getMyDrafts() {
    return this.request("/client/my-drafts");
  }

  getMyExecutionDetail(id) {
    return this.request(`/client/my-executions/${id}`);
  }

  // Submit rework answers (client)
  submitReworkAnswers(executionId, answers) {
    return this.request(`/client/my-executions/${executionId}/submit-rework`, {
      method: "POST",
      body: JSON.stringify({ answers }),
    });
  }

  // Register after assessment
  registerAfterAssessment(assessmentId, data) {
    return this.request(`/assessments/${assessmentId}/register`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  getContentSettings() {
    return this.request("/admin/content-settings");
  }

  updateContentSettings(data) {
    return this.request("/admin/content-settings", {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  getTemplates() {
    return this.request("/admin/templates");
  }

  // NACE codes admin
  getAdminNaceCodes() {
    return this.request("/admin/nace-codes");
  }

  createNaceCode(data) {
    return this.request("/admin/nace-codes", { method: "POST", body: JSON.stringify(data) });
  }

  updateNaceCode(id, data) {
    return this.request(`/admin/nace-codes/${id}`, { method: "PUT", body: JSON.stringify(data) });
  }

  deleteNaceCode(id) {
    return this.request(`/admin/nace-codes/${id}`, { method: "DELETE" });
  }

  // Agent
  getAgentStats() {
    return this.request("/agent/statistics");
  }

  getAgentCustomers() {
    return this.request("/agent/customers");
  }

  getAgentExecutions(status) {
    const q = status ? `?status=${status}` : "";
    return this.request(`/agent/executions${q}`);
  }

  getAgentExecution(id) {
    return this.request(`/agent/executions/${id}`);
  }

  reviewExecution(id) {
    return this.request(`/agent/executions/${id}/review`, { method: "POST" });
  }

  agentApproveExecution(id, comment) {
    return this.request(`/agent/executions/${id}/approve`, {
      method: "POST",
      body: JSON.stringify({ comment: comment || "" }),
    });
  }

  requestRework(executionId, questionId, reason) {
    return this.request(
      `/agent/executions/${executionId}/rework?question_id=${questionId}&reason=${encodeURIComponent(reason)}`,
      { method: "POST" }
    );
  }

  // Public
  getQuestions(category) {
    const q = category ? `?category=${category}` : "";
    return this.request(`/questions${q}`);
  }

  getPublicAssessments() {
    return this.request("/client/assessments");
  }

  // PDF Downloads
  getAdminPdfUrl(executionId) {
    return `${this.baseUrl}/admin/executions/${executionId}/pdf`;
  }

  getAgentPdfUrl(executionId) {
    return `${this.baseUrl}/agent/executions/${executionId}/pdf`;
  }

  getClientPdfUrl(executionId) {
    return `${this.baseUrl}/client/my-executions/${executionId}/pdf`;
  }

  async downloadPdf(url) {
    const token = this.getToken();
    if (!token) {
      throw new Error("Please login first");
    }
    try {
      const res = await fetch(url, { 
        method: 'GET',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/pdf'
        } 
      });
      
      if (!res.ok) {
        // Try to get error message from response
        const contentType = res.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const err = await res.json();
          throw new Error(err.detail || `Download failed (${res.status})`);
        }
        throw new Error(`Download failed (${res.status})`);
      }
      
      const blob = await res.blob();
      if (blob.size === 0) {
        throw new Error("Empty PDF received");
      }
      
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      // Extract filename from Content-Disposition header if available
      const disposition = res.headers.get('content-disposition');
      let filename = "Assessment_Report.pdf";
      if (disposition && disposition.includes('filename=')) {
        const match = disposition.match(/filename="?([^";\n]+)"?/);
        if (match) filename = match[1];
      }
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('PDF download error:', err);
      throw err;
    }
  }

  // PDF Template (admin)
  getPdfTemplate() {
    return this.request("/admin/pdf-template");
  }

  updatePdfTemplate(data) {
    return this.request("/admin/pdf-template", { method: "PUT", body: JSON.stringify(data) });
  }

  uploadPdfLogo(base64Data) {
    return this.request("/admin/pdf-template/upload-logo", {
      method: "POST", body: JSON.stringify({ logo_base64: base64Data })
    });
  }

  // Data Tables (GHG / Custom)
  getDataTables() {
    return this.request("/admin/data-tables");
  }

  createDataTable(data) {
    return this.request("/admin/data-tables", { method: "POST", body: JSON.stringify(data) });
  }

  getDataTable(id) {
    return this.request("/admin/data-tables/" + id);
  }

  updateDataTable(id, data) {
    return this.request("/admin/data-tables/" + id, { method: "PUT", body: JSON.stringify(data) });
  }

  deleteDataTable(id) {
    return this.request("/admin/data-tables/" + id, { method: "DELETE" });
  }

  linkTableToQuestion(questionId, tableId) {
    return this.request(`/admin/questions/${questionId}/link-table`, {
      method: "POST", body: JSON.stringify({ table_id: tableId })
    });
  }

  unlinkTableFromQuestion(questionId, tableId) {
    return this.request(`/admin/questions/${questionId}/unlink-table`, {
      method: "POST", body: JSON.stringify({ table_id: tableId })
    });
  }

  getPublicDataTable(id) {
    return this.request("/data-tables/" + id);
  }

  // Emission Factors (Admin)
  getEmissionFactors() {
    return this.request("/carbon/emission-factors");
  }

  updateEmissionFactors(data) {
    return this.request("/carbon/emission-factors", {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  // File Upload
  async uploadFile(file, executionId, questionId) {
    const formData = new FormData();
    formData.append("file", file);
    const params = new URLSearchParams();
    if (executionId) params.append("execution_id", executionId);
    if (questionId) params.append("question_id", questionId);
    const url = `${this.baseUrl}/upload?${params.toString()}`;
    const token = this.getToken();
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(url, { method: "POST", headers, body: formData });
    if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || "Upload failed"); }
    return res.json();
  }

  getFiles(executionId, questionId) {
    const params = new URLSearchParams();
    if (executionId) params.append("execution_id", executionId);
    if (questionId) params.append("question_id", questionId);
    return this.request(`/files?${params.toString()}`);
  }

  getFileUrl(fileId) {
    return `${this.baseUrl}/files/${fileId}`;
  }

  // Site Settings (CMS)
  getSiteSettings() {
    return this.request("/site-settings");
  }

  updateSiteSettings(data) {
    return this.request("/admin/site-settings", { method: "PUT", body: JSON.stringify(data) });
  }

  // Reassignment
  reassignExecution(executionId, assigneeId) {
    return this.request(`/admin/executions/${executionId}/reassign`, {
      method: "POST", body: JSON.stringify({ assignee_id: assigneeId })
    });
  }

  // Execution History
  getExecutionHistory(executionId) {
    return this.request(`/executions/${executionId}/history`);
  }

  // Change Password
  changePassword(oldPassword, newPassword) {
    return this.request("/client/change-password", {
      method: "POST", body: JSON.stringify({ old_password: oldPassword, new_password: newPassword })
    });
  }

  // Carbon Calculator
  calculateCarbon(inputs) {
    return this.request("/carbon/calculate", {
      method: "POST", body: JSON.stringify(inputs)
    });
  }

  getEmissionFactors() {
    return this.request("/carbon/emission-factors");
  }

  getCountries() {
    return this.request("/carbon/countries");
  }

  saveCarbonForExecution(executionId, inputs) {
    return this.request(`/carbon/save-for-execution/${executionId}`, {
      method: "POST", body: JSON.stringify(inputs)
    });
  }

  getExecutionCarbon(executionId) {
    return this.request(`/carbon/execution/${executionId}`);
  }
}

const api = new ApiService();
export default api;
